import React from 'react';
import Points from 'components/Points';
import { TradeSuggestion, TradeSuggestionItem } from 'types/interfaces/tradeSuggestion';
import { getCardImageUrl } from 'utils/cardImageFallback';
import { formatConditionShort, formatLanguage } from 'utils/offerErrors';

/**
 * Card identity line, matching the mock exactly: "STH #138 · NM · English · Nonfoil".
 *
 * Reuses formatConditionShort and formatLanguage from offerErrors, the same
 * helpers the three failure surfaces use, so a card reads identically wherever
 * it is named.
 *
 * setCode and collectorNumber are nullable on the wire (MTGCard columns are
 * String?), so the set segment is omitted when absent rather than rendering a
 * placeholder. '???' is this codebase's MISSING PRICE convention and would
 * misread as a pricing failure here.
 */
export const identityLine = (item: TradeSuggestionItem): string => {
  const segments: string[] = [];
  if (item.card.setCode) {
    segments.push(
      item.card.collectorNumber
        ? `${item.card.setCode.toUpperCase()} #${item.card.collectorNumber}`
        : item.card.setCode.toUpperCase()
    );
  }
  segments.push(formatConditionShort(item.condition));
  segments.push(formatLanguage(item.card.lang));
  segments.push(item.foil ? 'Foil' : 'Nonfoil');
  return segments.join(' · ');
};

interface SideProps {
  label: string;
  labelModifier: 'they' | 'you';
  items: TradeSuggestionItem[];
  /** Always the FULL bundle total from the server, never the rendered subset */
  totalPoints: number;
}

const LedgerSide: React.FC<SideProps> = ({ label, labelModifier, items, totalPoints }) => (
  <section className={`sugg-ledger-side sugg-ledger-side--${labelModifier}`} aria-label={label}>
    <div className="sugg-ledger-side-head">
      <span className={`sugg-ledger-side-label sugg-ledger-side-label--${labelModifier}`}>
        {label}
      </span>
      <Points type="small" points={totalPoints} />
    </div>
    <ul className="sugg-ledger-rows">
      {items.map((item) => (
        <li key={item.id} className={`sugg-ledger-row sugg-ledger-row--${labelModifier}`}>
          <img
            className="sugg-ledger-thumb"
            src={getCardImageUrl(
              { image: item.card.imageUrl, scryfallId: item.card.scryfallId },
              'small'
            )}
            alt=""
            loading="lazy"
          />
          <div className="sugg-ledger-row-main">
            <span className="sugg-ledger-name">
              {item.quantity > 1 ? `${item.quantity}x ` : ''}
              {item.card.name}
            </span>
            <span className="sugg-ledger-identity">{identityLine(item)}</span>
          </div>
          <Points type="small" points={item.lineTotalPoints} />
        </li>
      ))}
    </ul>
  </section>
);

interface Props {
  suggestion: TradeSuggestion;
}

/**
 * The full two-column ledger: every card on both sides with its identity and
 * its own point value.
 *
 * This REPLACES the trade axis in place rather than appending below it, so the
 * card keeps one height and the footer never moves. It is a STATE, not a user
 * preference: no layout toggle, nothing persisted.
 *
 * Side totals always come from suggestion.totals, never from summing the rows
 * on screen, so they are identical whether the ledger is open or closed.
 */
const SuggestionLedger: React.FC<Props> = ({ suggestion }) => (
  <div className="sugg-ledger">
    <LedgerSide
      label="YOU SEND THEM"
      labelModifier="you"
      items={suggestion.youSend}
      totalPoints={suggestion.totals.youSendPoints}
    />
    <LedgerSide
      label="THEY SEND YOU"
      labelModifier="they"
      items={suggestion.theySend}
      totalPoints={suggestion.totals.theySendPoints}
    />
  </div>
);

export default SuggestionLedger;
